import { Navigate } from 'react-router';
import type { FC } from 'react';
import useAuth from 'src/hooks/useAuth';
import useHasPermissions from 'src/hooks/useHasPermission';
import { useSubscriptionPermissionsContext } from 'src/mindtime/contexts/SubscriptionPermissionsContext';
import {
  resolveClientSubscribedAccess,
  resolveEdrPermissionsReady,
} from 'src/mindtime/utils/subscriptionPermissions';
import { useIsResellerOrg, useOrganizationTypeName } from 'src/mindtime/hooks/useIsResellerOrg';
import { SubscriptionUnavailablePage } from 'src/mindtime/pages/security/edr/shared/SubscriptionUnavailablePage';
import { SubscriptionPermissionsPageSkeleton } from 'src/mindtime/pages/security/edr/shared/SubscriptionPermissionsSkeleton';
import { SUBSCRIPTION_UNAVAILABLE_DEFAULT_KEY } from 'src/mindtime/pages/security/edr/shared/subscriptionUnavailable';
import { getSecurityLandingPath } from './securityNav';
import { toSecurityChildPath } from './securityRoutes';

const SecurityIndexRedirect: FC = () => {
  const checkPermissions = useHasPermissions();
  const { isInitialized, user } = useAuth();
  const isReseller = useIsResellerOrg();
  const organizationTypeName = useOrganizationTypeName();
  const { subscriptionKeys, hasActiveSubscription, resolved, roleKeys, orgEntitlements } =
    useSubscriptionPermissionsContext();

  const clientSubscribed = resolveClientSubscribedAccess(
    isReseller,
    orgEntitlements,
    hasActiveSubscription,
  );

  const permissionsReady = resolveEdrPermissionsReady({
    isReseller,
    authInitialized: isInitialized,
    hasAuthUser: Boolean(user),
    subscriptionResolved: resolved,
  });

  if (!permissionsReady) return <SubscriptionPermissionsPageSkeleton />;

  const landingPath = getSecurityLandingPath(checkPermissions, isReseller, {
    subscriptionKeys,
    roleKeys,
    hasActiveSubscription: clientSubscribed,
    subscriptionResolved: resolved,
    isReseller,
    organizationTypeName,
    permissionsReady,
  });

  if (!landingPath) {
    // Reseller / preview (role tier): no visible leaf → /404.
    // Subscribed client missing entitlements → subscription unavailable page.
    if (isReseller || !clientSubscribed) {
      return <Navigate to="/404" replace />;
    }
    return <SubscriptionUnavailablePage messageKey={SUBSCRIPTION_UNAVAILABLE_DEFAULT_KEY} />;
  }

  return <Navigate to={toSecurityChildPath(landingPath)} replace />;
};

export default SecurityIndexRedirect;
